import Card from "@/components/Card";
import SectionHeader from "@/components/SectionHeader";
import SkeletonRow from "@/components/SkeletonRow";
import Stepsbar from "@/components/Stepsbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useScreen from "@/hooks/useScreen";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import { getDataStep, getListBerkasPenerimaan } from "@/services/getData";
import { BiodataSate, DataSiswa, Step, SysBerkas } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ChevronLeft, ChevronRight, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { FaClock } from "react-icons/fa6";
import {
  IoCalendarNumber,
  IoCheckmarkCircle,
  IoWarning,
} from "react-icons/io5";
import { LuChevronRight } from "react-icons/lu";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import Prodi from "./partial/prodi/Prodi";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanPascasarjanaPage() {
  const { user } = useUser();
  const { isMobile } = useScreen();
  const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  // const stepParams = searchParams.get("step");
  const { isValidPendaftaran } = useDataPendaftaran();
  const {
    isPendingPeriode,
    timePassed,
    dataPeriode,
    timePassedActive,
    dataPeriodeActive,
  } = useTimePeriode();

  const navigate = useNavigate();

  const [sessionPeriode, setSessionPeriode] = useSessionStorage(
    "sessionPeriode",
    {
      id_sys_periode: "",
    },
  );

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });
  const idDataPendaftaran = dataStep?.id_data_pendaftaran;

  const { data: dataBerkas } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Biodata",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Prodi",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 3,
      text: "Berkas",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 4,
      text: "Validasi & Kartu Peserta",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);
  const [listPanduanPenerimaanPasca, setListPanduanPenerimaanPasca] = useState([
    {
      title: "Biodata",
      contents: [
        {
          title: "Berkas yang dibutuhkan",
          instructions: [],
        },
        {
          title: "Tata cara pengisian",
          instructions: [
            "Klik kolom yang mau diisi",
            "Isi data dengan benar sesuai kolom yang diminta",
          ],
        },
      ],
    },
    {
      title: "Prodi",
      contents: [
        {
          title: "Tata cara pengisian",
          instructions: [
            "Lihat daftar program studi yang tersedia",
            "Pilih program studi",
            "Pilik konsentrasi program studi (jika ada)",
            "Pilik lokasi kuliah (jika ada)",
            "Tekan 'Simpan'",
          ],
        },
      ],
    },
    {
      title: "Berkas",
      contents: [
        {
          title: "Berkas yang dibutuhkan",
          instructions: [
            "Dapat dilihat pada tabel. (Berkas yang berisi label (*Wajib) adalah berkas yang wajib untuk dipload.",
          ],
        },
        {
          title: "Tata cara pengisian",
          instructions: [
            "Khusus untuk berkas yang memiliki format (ditandai dengan Tombol Unduh pada kolom Format) lakukan unduh terlebih dahulu",
            "Tekan tombol 'Upload'",
            "Pilih file berkas",
            "Isikan nomor berkas jika ada",
            "Tekan tombol 'Simpan File Berkas'",
          ],
        },
      ],
    },
    {
      title: "Kartu Peserta",
      contents: [
        {
          title: "Instruksi",
          instructions: [
            "Pastikan seluruh isian pada data pendaftaran sudah diisi dengan benar",
            "Tekan 'Validasi Pendaftaran' untuk melakukan validasi pendaftaran",
            "Unduh kartu peserta dengan menekan tombol 'Download'",
          ],
        },
      ],
    },
  ]);

  const [formBiodata, setFormBiodata] = useState<BiodataSate>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_agama: "",
    id_ref_negara: "",
    id_ref_alat_transportasi: "",
    id_ref_jenis_tinggal: "",
    nisn: "",
    nama_lengkap: "",
    handphone: "",
    npwp: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    berat_badan: 0,
    tinggi_badan: 0,
    ukuran_baju: "",
    jalan: "",
    rt: "",
    rw: "",
    kode_pos: "",
    kelurahan: "",
    provinsi: "",
    kabupaten: "",
    kecamatan: "",
    nama_panti_asuhan: "",
    kebutuhan_khusus: [] as string[],
    pilihan_lokasi_kuliah: "",
    asal_pt: "",
    asal_prodi: "",
    asal_prodi_manual: "",
    is_bekerja: undefined,
    tempat_bekerja: "",
    penghasilan_bekerja: "",
    nilai_IPK: "",
    akreditasi_pt_asal: "",
    akreditasi_prodi_asal: "",
    tahun_ijazah_terakhir: "",
    kontak_darurat_hubungan: "",
    kontak_darurat_nama: "",
    kontak_darurat_hp: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
    />,
    <Prodi idDataPendaftaran={idDataPendaftaran} />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <KartuPeserta idDataPendaftaran={idDataPendaftaran} />,
  ]);

  const [showPanduan, setShowPanduan] = useState(false);

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);
    if (formBiodata.asal_prodi == "LAINNYA") {
      formData.append("asal_prodi", formBiodata.asal_prodi_manual || "");
    }

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
          searchParams.set("step", "2");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 2:
        if (dataStep?.data_pilihan_prodi === 1) {
          goToStep(3);
          searchParams.set("step", "3");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 3:
        if (dataStep?.data_berkas === 1) {
          goToStep(4);
          searchParams.set("step", "4");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
    }
  };

  useEffect(() => {
    setisPendingSteps(true);
    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Biodata":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Prodi":
            if (dataStep?.data_pilihan_prodi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Berkas":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              // goToStep(4);
              setCurrentIndexDisable(4);
            }
            break;
          case "Validasi & Kartu Peserta":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(4);
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (isMobile) setShowPanduan(false);
    else setShowPanduan(true);
  }, [isMobile]);

  useEffect(() => {
    if (!dataBerkas) return;

    const listNamaBerkas = dataBerkas?.listSysBerkas?.map((row: SysBerkas) => {
      return row.jenis_berkas?.nama_jenis_berkas;
    });

    setListPanduanPenerimaanPasca((prev) =>
      prev?.map((value) => {
        if (value.title.startsWith("Berkas")) {
          return {
            ...value,
            contents: value.contents.map((content) => {
              if (content.title.startsWith("Berkas")) {
                return {
                  ...content,
                  instructions: listNamaBerkas,
                };
              }
              return content;
            }),
          };
        }
        return value;
      }),
    );
  }, [dataBerkas]);

  useEffect(() => {
    if (timePassed.waktu_pengumuman) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman`);
    }
  }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  if (isPendingPeriode) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />;
          })}
      </div>
    );
  }

  const isDisableInput =
    isValidPendaftaran || timePassedActive?.waktu_selesai_daftar_valid_selesai;

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "pascasarjana"
  ) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <main>
      <SectionHeader
        title={`Penerimaan - ${dataPeriode?.jalur_jenis?.nama_jalur_jenis}`}
      />

      <div className="mb-8">
        {/* Main Container dengan efek Glassmorphism & Shadow halus */}
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden">
          {/* 1. Header Bar: Attention Grabber */}
          <div className="bg-amber-50 dark:bg-amber-950/30 px-6 py-3 border-b border-amber-100 dark:border-amber-900/50 flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-amber-400 rounded-full animate-ping opacity-20"></div>
              <IoWarning
                className="relative text-amber-600 dark:text-amber-500"
                size={22}
              />
            </div>
            <h3 className="font-bold text-amber-900 dark:text-amber-100 text-base tracking-tight">
              Informasi Penting Pendaftaran
            </h3>
          </div>

          {/* 2. Content Body */}
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Card A: Status Pendaftaran (Urgent/Past) */}
              {timePassedActive?.waktu_selesai_daftar_valid_selesai && (
                <div className="group flex items-start gap-4 p-4 rounded-xl border border-rose-100 bg-rose-50/50 dark:bg-rose-950/10 dark:border-rose-900/30 transition-all hover:bg-rose-50 dark:hover:bg-rose-950/20">
                  <div className="p-2 bg-white dark:bg-rose-900/30 rounded-lg shadow-sm text-rose-600">
                    <FaClock size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-rose-600/80 mb-1 tracking-wider">
                      Status Pendaftaran
                    </p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      Telah berakhir pada:
                    </p>
                    <p className="font-bold text-rose-700 dark:text-rose-400 text-base">
                      {dataPeriodeActive?.waktu_selesai_daftar_valid &&
                        formatDateIndonesia(
                          dataPeriodeActive?.waktu_selesai_daftar_valid,
                        )}
                    </p>
                  </div>
                </div>
              )}

              {/* Card B: Jadwal Pengumuman (Info) */}
              <div className="group flex items-start gap-4 p-4 rounded-xl border border-indigo-100 bg-indigo-50/50 dark:bg-indigo-950/10 dark:border-indigo-900/30 transition-all hover:bg-indigo-50 dark:hover:bg-indigo-950/20">
                <div className="p-2 bg-white dark:bg-indigo-900/30 rounded-lg shadow-sm text-indigo-600">
                  <IoCalendarNumber size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-indigo-600/80 mb-1 tracking-wider">
                    Jadwal Pengumuman
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    Akan dimulai pada:
                  </p>
                  <p className="font-bold text-indigo-700 dark:text-indigo-400 text-base">
                    {dataPeriode?.waktu_pengumuman &&
                      formatDateIndonesia(dataPeriode?.waktu_pengumuman)}
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Footer Alert: Validation Reminder (Crucial Info) */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <IoCheckmarkCircle
                className="text-emerald-500 shrink-0"
                size={24}
              />
              <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Agar pendaftaran dianggap{" "}
                <strong className="text-slate-900 dark:text-white">
                  SAH & SELESAI
                </strong>
                , pastikan Anda telah melakukan{" "}
                <span className="inline-block border-b-2 border-emerald-400 font-bold text-slate-900 dark:text-white px-0.5">
                  Validasi Data
                </span>{" "}
                dan mengunduh{" "}
                <strong className="text-slate-900 dark:text-white">
                  Kartu Peserta
                </strong>
                .
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />
        <div className="flex-1 h-screen overflow-auto">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {currentStepNumber !== 2 && (
              <Card className={`${showPanduan ? "lg:w-[20rem]" : ""} h-fit`}>
                <div className="flex justify-between">
                  {showPanduan && (
                    <h1 className="mb-5 font-bold text-xl">Panduan</h1>
                  )}
                  <div className="flex items-center flex-col gap-y-3">
                    {!showPanduan && (
                      <span className="text-sm font-bold">Panduan</span>
                    )}
                    <Button
                      variant={"outline"}
                      size={"icon"}
                      onClick={() => setShowPanduan((show) => !show)}
                    >
                      <LuChevronRight
                        className={`${
                          showPanduan ? "rotate-90" : "rotate-0"
                        } duration-300`}
                      />
                    </Button>
                  </div>
                </div>
                <section className="flex flex-col gap-y-3">
                  {showPanduan &&
                    listPanduanPenerimaanPasca.map((panduan, index) => {
                      return (
                        <div key={index}>
                          <Accordion type="single" collapsible>
                            <AccordionItem value="item-1">
                              <AccordionTrigger className="text-start">
                                {panduan.title}
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="flex flex-col gap-1">
                                  {panduan.contents.map((content) => {
                                    return (
                                      <div key={content.title}>
                                        <div className="flex items-center gap-1">
                                          <span>-</span>
                                          <h1 className="font-semibold">
                                            {content.title}
                                          </h1>
                                        </div>
                                        <ul>
                                          {content.instructions.map(
                                            (instruction, indexInstruction) => {
                                              return (
                                                <li
                                                  key={indexInstruction}
                                                  className="text-muted-foreground"
                                                >
                                                  <span>
                                                    {indexInstruction + 1}.{" "}
                                                  </span>
                                                  {instruction}
                                                </li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                </section>
              </Card>
            )}

            <div className="flex-1 overflow-auto">
              {currentStep}

              {/* Wrapper Navigation */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                {/* Tombol KIRI (Kembali) */}
                <div className="w-full sm:w-auto">
                  {!firstStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={backStep}
                      className="w-full sm:w-auto text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Kembali
                    </Button>
                  )}
                </div>

                {/* Tombol KANAN (Group Aksi) */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Tombol Simpan (Hanya di step awal & bisa diedit) */}
                  {firstStep && !isDisableInput && (
                    <Button
                      type="button"
                      variant="secondary" // Menggunakan varian secondary agar tidak rebutan fokus dengan 'Berikutnya'
                      className="w-full sm:w-auto bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Simpan
                        </>
                      )}
                    </Button>
                  )}

                  {/* Tombol Berikutnya */}
                  {!lastStep && (
                    <Button
                      type="button"
                      variant="default" // Primary action
                      className="w-full sm:w-auto"
                      disabled={currentIndex === currentIndexDisable - 1} // Gunakan properti disabled asli
                      onClick={handleNextStep}
                    >
                      Berikutnya
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
